import { Fragment, useMemo, useState } from 'react';
import {
  usePlayer,
  usePlayerSkills,
  useSkillsCatalog,
  useLearnSkill,
} from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/cn';
import { hapticNotify } from '@/lib/telegram';
import type { Skill } from '@/types';

const BRANCH_ORDER = ['travel', 'dealer', 'protagonist', 'tactician'];

const BRANCH_TITLES: Record<string, string> = {
  travel: 'Странствия',
  dealer: 'Делец',
  protagonist: 'Протагонист',
  tactician: 'Тактик',
};

interface SkillState {
  level: number;
  maxed: boolean;
  locked: boolean;
  learnable: boolean;
  reqSkill: Skill | null;
}

interface HoverState {
  skill: Skill;
  rect: DOMRect;
}

export function SkillTree() {
  const userId = useAuthStore((s) => s.userId);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const { data: skillsData } = useSkillsCatalog();
  const { data: playerSkillsData } = usePlayerSkills(userId);
  const { data: playerData } = usePlayer(userId);
  const learn = useLearnSkill(userId ?? 0);

  const allSkills = useMemo(() => skillsData?.skills ?? [], [skillsData]);

  const skillsById = useMemo(() => {
    const m = new Map<string, Skill>();
    allSkills.forEach((s) => m.set(s.skill_id, s));
    return m;
  }, [allSkills]);

  const branches = useMemo(() => {
    const present = new Set(allSkills.map((s) => s.branch));
    return [
      ...BRANCH_ORDER.filter((b) => present.has(b)),
      ...Array.from(present).filter((b) => !BRANCH_ORDER.includes(b)),
    ];
  }, [allSkills]);

  const [activeBranch, setActiveBranch] = useState<string | null>(null);
  const branch = activeBranch ?? branches[0] ?? '';
  const [hovered, setHovered] = useState<HoverState | null>(null);

  const learned = playerSkillsData?.skills ?? {};
  const skillPoints = playerData?.player?.skill_points ?? 0;

  // Build the fork: root -> [leftChain, rightChain]
  const { root, chains } = useMemo(() => {
    const branchSkills = allSkills.filter((s) => s.branch === branch);
    const rootSkill = branchSkills.find((s) => !s.requires_skill) ?? null;
    if (!rootSkill) return { root: null, chains: [] as Skill[][] };

    const buildChain = (start: Skill): Skill[] => {
      const chain = [start];
      let cur = start;
      for (;;) {
        const next = branchSkills.find((s) => s.requires_skill === cur.skill_id);
        if (!next) break;
        chain.push(next);
        cur = next;
      }
      return chain;
    };

    const rootChildren = branchSkills
      .filter((s) => s.requires_skill === rootSkill.skill_id)
      .sort((a, b) => a.skill_id.localeCompare(b.skill_id));

    return { root: rootSkill, chains: rootChildren.map((c) => buildChain(c)) };
  }, [allSkills, branch]);

  const getState = (skill: Skill): SkillState => {
    const level = learned[skill.skill_id] ?? 0;
    const maxed = level >= skill.max_level;
    const reqSkill = skill.requires_skill
      ? (skillsById.get(skill.requires_skill) ?? null)
      : null;
    const reqLevel = skill.requires_skill ? (learned[skill.requires_skill] ?? 0) : 0;
    const locked = !!reqSkill && reqLevel < reqSkill.max_level;
    const learnable = skillPoints > 0 && !maxed && !locked;
    return { level, maxed, locked, learnable, reqSkill };
  };

  if (!skillsData) {
    return <div className="text-white/40 text-sm">Загружаем дерево умений…</div>;
  }

  const handleLearn = async (skill: Skill) => {
    try {
      await learn.mutateAsync({ skill_id: skill.skill_id });
      hapticNotify('success');
    } catch (err) {
      hapticNotify('error');
      const message = err instanceof Error ? err.message : 'Не удалось изучить';
      pushNotification({ type: 'error', message });
    }
  };

  const renderNode = (skill: Skill) => {
    const st = getState(skill);
    return (
      <button
        type="button"
        onClick={() => {
          if (st.learnable && !learn.isPending) handleLearn(skill);
        }}
        onMouseEnter={(e) =>
          setHovered({ skill, rect: e.currentTarget.getBoundingClientRect() })
        }
        onMouseLeave={() => setHovered(null)}
        onTouchStart={(e) =>
          setHovered({ skill, rect: e.currentTarget.getBoundingClientRect() })
        }
        className="flex flex-col items-center gap-1 group"
      >
        <div
          className={cn(
            'relative w-16 h-16 rounded-2xl border-2 flex items-center justify-center transition',
            st.maxed
              ? 'border-gold/60 bg-gold/10'
              : st.level > 0
                ? 'border-accent/60 bg-accent/10'
                : st.learnable
                  ? 'border-accent/50 bg-bg-elevated'
                  : st.locked
                    ? 'border-white/5 bg-bg-elevated/40'
                    : 'border-white/10 bg-bg-elevated',
            'group-active:scale-95',
          )}
        >
          <img
            src={`/data/items/${skill.icon}`}
            alt={skill.name}
            className={cn('w-12 h-12 object-contain', st.locked && 'grayscale opacity-70')}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
            draggable={false}
          />
          {st.locked && (
            <span className="absolute -bottom-1 -right-1 text-[13px] leading-none">
              🔒
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 text-[11px] tabular-nums leading-none">
          <span className={st.maxed ? 'text-gold' : 'text-white/70'}>
            {st.level}/{skill.max_level}
          </span>
          {st.learnable && <span className="text-success font-bold">+</span>}
        </div>
      </button>
    );
  };

  const hoveredState = hovered ? getState(hovered.skill) : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
          Умения
        </h3>
        <div className="text-xs text-accent">Очков: {skillPoints}</div>
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {branches.map((b) => (
          <button
            key={b}
            onClick={() => setActiveBranch(b)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap border transition',
              branch === b
                ? 'bg-accent/20 border-accent/50 text-white'
                : 'bg-bg-elevated border-white/5 text-white/60 hover:text-white',
            )}
          >
            <img
              src={`/data/interface/branch_${b}.webp`}
              alt=""
              className="w-5 h-5 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              draggable={false}
            />
            {BRANCH_TITLES[b] ?? b}
          </button>
        ))}
      </div>

      {!root ? (
        <div className="text-white/40 text-sm">Нет умений в этой ветке.</div>
      ) : (
        <div className="flex flex-col items-center pt-2">
          {renderNode(root)}
          <div className="w-0.5 h-3 bg-white/15" />
          <div className="w-1/2 h-0.5 bg-white/15" />
          <div className="grid grid-cols-2 w-full">
            {chains.map((chain, ci) => (
              <div key={ci} className="flex flex-col items-center">
                <div className="w-0.5 h-3 bg-white/15" />
                {chain.map((s, i) => (
                  <Fragment key={s.skill_id}>
                    {i > 0 && <div className="w-0.5 h-3 bg-white/15" />}
                    {renderNode(s)}
                  </Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {hovered && hoveredState && (
        <div
          className="fixed z-50 pointer-events-none animate-fade-in"
          style={{
            top: Math.max(8, hovered.rect.top - 8),
            left: Math.min(
              window.innerWidth - 248,
              Math.max(8, hovered.rect.left + hovered.rect.width / 2 - 120),
            ),
            width: 240,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="glass rounded-xl p-3 space-y-1.5 shadow-card">
            <div className="flex items-center justify-between gap-2">
              <div className="text-white font-semibold text-sm">
                {hovered.skill.name}
              </div>
              <div className="text-[11px] tabular-nums text-white/55 shrink-0">
                {hoveredState.level}/{hovered.skill.max_level}
              </div>
            </div>
            {hovered.skill.description && (
              <p className="text-xs text-white/65 leading-snug">
                {hovered.skill.description}
              </p>
            )}
            {hoveredState.locked && hoveredState.reqSkill ? (
              <div className="text-[11px] text-gold">
                Требуется: {hoveredState.reqSkill.name} (макс.)
              </div>
            ) : hoveredState.maxed ? (
              <div className="text-[11px] text-gold">Максимум</div>
            ) : hoveredState.learnable ? (
              <div className="text-[11px] text-accent">Клик: изучить (1 очко)</div>
            ) : (
              <div className="text-[11px] text-white/40">Нет очков умений</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
